/**
 * @feature landing-page
 * @owner das-finsuite-application-dev
 * @description Fetches pre-computed FX rates from S3 for the landing page card.
 *
 * The Lambda (FinSuite-FxRateProcessor) runs daily at 16:30 UTC and writes
 * fx-rates-current.json with RO-Current planning rates for the current month.
 *
 * ## FX Card Requirements
 * 1. Auto-detect user's local currency from browser timezone (e.g. India → INR, Australia → AUD, US → USD)
 * 2. Default display: user's local currency against USD (e.g. "1 USD = ₹93.485")
 * 3. No auto-scrolling — static display, user controls selection
 * 4. Bottom row: clickable currency buttons — local + defaults (EUR, GBP, JPY, INR), deduplicated
 * 5. Active state: selected button is bold with subtle background highlight
 * 6. External link: Google Finance (google.com/finance/quote/{TARGET}-USD), updates with selection
 * 7. Data source: pre-computed daily S3 file, client caches 1 hour
 * 8. Label: "FX · USD BASE" (no location code)
 * 9. Hover effect: card lifts with shadow on mouse hover
 * 10. User's currency selection persisted to localStorage
 * 11. All rates displayed with 3 decimal places
 *
 * Data source: S3 (op-excellence-{stage}/upstream/GFT_FX_RATE/fx-rates-current.json)
 */

import { GetObjectCommand } from '@aws-sdk/client-s3';
import { getS3Client } from 'src/services/aws/S3Service';
import { S3_ENV_CONSTANTS } from 'src/constants/EnvironmentConstants';
import { logger } from 'src/logger';

// --- Types ---

export interface FxRatesData {
  generated_at: string;
  planning_cycle: string;
  month: number;
  rates: Record<string, number>;
}

export interface FxCardPair {
  target: string;
  rate: number;
  decimals: number;
  symbol: string;
  formatted: string;
}

export interface FxCardConfig {
  baseCurrency: string;
  pairs: FxCardPair[];
}

// --- Constants ---

const CACHE_KEY = 'finsuite-fx-rates';
const CACHE_TTL_MS = 60 * 60 * 1000;
const S3_KEY = 'upstream/GFT_FX_RATE/fx-rates-current.json';
const DECIMALS = 3;

const USD_TARGETS = ['EUR', 'GBP', 'JPY', 'INR'] as const;

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  INR: '₹',
  CAD: 'C$',
  AUD: 'A$',
  BRL: 'R$',
  MXN: 'MX$',
  SGD: 'S$',
  SEK: 'kr',
  USD: '$',
  KRW: '₩',
  CNY: 'CN¥',
  PLN: 'zł',
  TRY: '₺',
  ZAR: 'R'
};

/** Country code (PAPI workLocation.country) → local currency. */
const COUNTRY_CURRENCY: Record<string, string> = {
  USA: 'USD',
  GBR: 'GBP',
  IND: 'INR',
  JPN: 'JPY',
  DEU: 'EUR',
  FRA: 'EUR',
  ITA: 'EUR',
  ESP: 'EUR',
  NLD: 'EUR',
  BEL: 'EUR',
  LUX: 'EUR',
  IRL: 'EUR',
  AUT: 'EUR',
  PRT: 'EUR',
  FIN: 'EUR',
  GRC: 'EUR',
  CAN: 'CAD',
  AUS: 'AUD',
  BRA: 'BRL',
  MEX: 'MXN',
  SGP: 'SGD',
  KOR: 'KRW',
  CHN: 'CNY',
  SWE: 'SEK',
  POL: 'PLN',
  TUR: 'TRY',
  ZAF: 'ZAR',
  ARE: 'AED'
};

// --- Cache ---

const getCached = (): FxRatesData | null => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw) as { data: FxRatesData; ts: number };
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
};

const setCache = (data: FxRatesData): void => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    /* ignore quota errors */
  }
};

// --- Helpers ---

const getCurrencyFromCountry = (country: string): string => {
  const currency = COUNTRY_CURRENCY[country] ?? 'USD';
  logger?.info('FX resolution', { logType: 'fx_resolution', country, currency, mapped: !!COUNTRY_CURRENCY[country] });
  return currency;
};

const buildPair = (target: string, rate: number): FxCardPair => {
  const symbol = CURRENCY_SYMBOLS[target] || target;
  const spacer = CURRENCY_SYMBOLS[target] ? '' : ' ';
  return {
    target,
    rate,
    decimals: DECIMALS,
    symbol,
    formatted: `1 USD = ${symbol}${spacer}${rate.toFixed(DECIMALS)}`
  };
};

// --- Public API ---

/**
 * Fetches FX rates from the pre-computed S3 file.
 * Returns cached data if within 1-hour TTL.
 */
export const fetchFxRates = async (): Promise<FxRatesData | null> => {
  const cached = getCached();
  if (cached) {
    logger?.info('FX resolution', { logType: 'fx_resolution', source: 'cache', status: 'cache_hit' });
    return cached;
  }

  try {
    const s3Client = getS3Client();
    if (!s3Client) {
      logger?.info('FX resolution', { logType: 'fx_resolution', source: 's3', status: 'no_client' });
      return null;
    }

    const bucket = S3_ENV_CONSTANTS.ENVIRONMENT_VARIABLES.Bucket;
    const resp = await s3Client.send(new GetObjectCommand({ Bucket: bucket, Key: S3_KEY }));
    const text = await resp.Body!.transformToString();
    const data: FxRatesData = JSON.parse(text);
    setCache(data);
    logger?.info('FX resolution', {
      logType: 'fx_resolution',
      source: 's3',
      status: 'success',
      rateCount: Object.keys(data.rates).length,
      month: data.month
    });
    return data;
  } catch (err) {
    logger?.error('FX resolution', { logType: 'fx_resolution', source: 's3', status: 'error', error: String(err) });
    return null;
  }
};

/**
 * Builds the FX card configuration based on user's PAPI workLocation country.
 * USD users: shows EUR, GBP, JPY, INR.
 * Non-USD users: local currency first, then remaining defaults (max 5).
 */
export const getFxCardConfig = (rates: FxRatesData, country?: string): FxCardConfig => {
  const localCurrency = getCurrencyFromCountry(country ?? 'USA');

  if (localCurrency === 'USD') {
    const pairs = USD_TARGETS.map((tgt) => buildPair(tgt, rates.rates[`USD/${tgt}`] ?? 0));
    return { baseCurrency: 'USD', pairs };
  }

  // Non-USD: local currency first, then defaults (deduplicated, max 5)
  const allTargets = [localCurrency, ...USD_TARGETS.filter((t) => t !== localCurrency)].slice(0, 5);
  const pairs = allTargets.map((tgt) => buildPair(tgt, rates.rates[`USD/${tgt}`] ?? 0));
  return { baseCurrency: 'USD', pairs };
};
